import { queryOptions } from "@tanstack/react-query";
import {
    type ContactMessageIntent,
    type ContactSessionTrigger,
    type CreateContactSessionResponse,
    contactVerificationStatusResponseSchema,
    createContactSessionResponseSchema,
    DEFAULT_CONTACT_MESSAGE_INTENT,
} from "@wags/profile-contracts/contact-session.contract.ts";
import type { z } from "zod";
import { markKitsHeardFromViewer } from "./kits-heard";

export type ContactVerificationStatus = z.infer<
    typeof contactVerificationStatusResponseSchema
>["status"];

type ContactSessionErrorReason = "http_status" | "invalid_payload" | "network";

class ContactSessionError extends Error {
    constructor(
        readonly reason: ContactSessionErrorReason,
        readonly status?: number,
    ) {
        super(reason);
    }
}

export function contactSessionErrorReason(error: Error | null) {
    return error instanceof ContactSessionError ? error.reason : "network";
}

export function contactSessionFailureMessage(error: Error | null) {
    const status =
        error instanceof ContactSessionError ? error.status : undefined;
    if (status === 429)
        return "Too many tries just now. Try again in a few minutes.";
    if (status === 404)
        return "Kits can't take a message about this dog right now.";
    return "Something went wrong. Please try again.";
}

export async function createContactSession(
    listingId: string,
    trigger: ContactSessionTrigger,
    fetchImpl: typeof fetch = fetch,
    question?: string,
): Promise<CreateContactSessionResponse> {
    const res = await fetchImpl("/api/kits/contact-sessions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            listingId,
            intent: DEFAULT_CONTACT_MESSAGE_INTENT,
            trigger,
            question,
        }),
    }).catch(() => null);
    if (!res) throw new ContactSessionError("network");
    if (!res.ok) throw new ContactSessionError("http_status", res.status);
    const parsed = createContactSessionResponseSchema.safeParse(
        await res.json().catch(() => null),
    );
    if (!parsed.success) throw new ContactSessionError("invalid_payload");
    return parsed.data;
}

export function markContactHandoffOpened(
    sessionId: string,
    intent: ContactMessageIntent,
    fetchImpl: typeof fetch = fetch,
): Promise<ContactSessionErrorReason | null> {
    return fetchImpl(`/api/kits/contact-sessions/${sessionId}/handoff-opened`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ intent }),
        keepalive: true,
    }).then(
        (res) => (res.ok ? null : "http_status"),
        () => "network",
    );
}

async function fetchContactVerificationStatus(
    challengeId: string,
    fetchImpl: typeof fetch,
) {
    const response = await fetchImpl(
        `/api/kits/contact-verifications/${encodeURIComponent(challengeId)}`,
        { cache: "no-store" },
    );
    if (!response.ok) throw new Error("Verification status request failed");
    return contactVerificationStatusResponseSchema.parse(await response.json());
}

const CONTACT_SESSION_REFRESH_MARGIN_MS = 60_000;

export function contactSessionFreshForMs(
    session: CreateContactSessionResponse | undefined,
    now = Date.now(),
) {
    if (!session) return 0;
    return Math.max(
        0,
        Date.parse(session.verification.expiresAt) -
            now -
            CONTACT_SESSION_REFRESH_MARGIN_MS,
    );
}

export function usableContactSession(
    cached: CreateContactSessionResponse | null | undefined,
    verificationStatus: ContactVerificationStatus | undefined,
) {
    if (!cached || contactSessionFreshForMs(cached) === 0) return null;
    return verificationStatus === "expired" ? null : cached;
}

export function contactSessionKey(
    authUserId: string,
    listingId: string,
    question?: string,
) {
    const key = [
        "contact-session",
        authUserId,
        listingId,
        DEFAULT_CONTACT_MESSAGE_INTENT,
    ] as const;
    return question ? ([...key, question] as const) : key;
}

export function contactSessionQuery(
    authUserId: string,
    listingId: string,
    queryFn: () => Promise<CreateContactSessionResponse>,
    question?: string,
) {
    return queryOptions({
        queryKey: contactSessionKey(authUserId, listingId, question),
        queryFn,
        retry: false,
        refetchOnWindowFocus: false,
        staleTime: (query) => contactSessionFreshForMs(query.state.data),
        networkMode: "always",
    });
}

const CONTACT_VERIFICATION_POLL_MS = 2_000;

export function contactVerificationQuery(
    challengeId: string | undefined,
    watching: boolean,
    onVerified?: () => Promise<void>,
    fetchImpl: typeof fetch = fetch,
) {
    const queryFn = async () => {
        const status = await fetchContactVerificationStatus(
            challengeId ?? "",
            fetchImpl,
        );
        if (status.status === "verified") {
            markKitsHeardFromViewer();
            await onVerified?.();
        }
        return status;
    };
    return {
        ...queryOptions({
            queryKey: ["contact-verification", challengeId],
            queryFn,
            enabled: () => Boolean(challengeId) && watching,
            refetchInterval: (query) =>
                query.state.status === "error" ||
                query.state.data?.status === "pending"
                    ? CONTACT_VERIFICATION_POLL_MS
                    : false,
            refetchOnWindowFocus: true,
            retry: false,
        }),
        queryFn,
    };
}
